import { Box, Typography } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { formatDate } from '../../../helpers/UtilityHelper';
import { SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { NAVY, SUBTLE, PRIMARY_BLUE } from '../../../styles/foundation/colors';

// Shared "what date range this page is showing" readout — a blue accent strip + calendar
// icon + an uppercase micro-label ("SHOWING ORDERS PLACED ON / FROM") + the date value.
// Reads as a description of the current view, not a filter. Pass the object returned by
// getOrderDateRangeInfo(); renders nothing when there's no range yet.
export function OrderDateRangeNote({ dateRangeInfo, sx }) {
  if (!dateRangeInfo?.from) return null;
  const single = dateRangeInfo.from === dateRangeInfo.to;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 1, mb: 1.5,
      borderLeft: `3px solid ${PRIMARY_BLUE}`, pl: '11px', ...sx,
    }}>
      <CalendarMonthOutlinedIcon sx={{ fontSize: 15, color: PRIMARY_BLUE }} />
      <Box component="span" sx={{ fontSize: '10px', fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', color: SUBTLE }}>
        {single ? 'Showing orders placed on' : 'Showing orders from'}
      </Box>
      <Typography component="span" sx={{
        fontFamily: "'Urbanist', sans-serif",
        fontSize: SIZE.sm,
        fontWeight: WEIGHT.semibold,
        color: NAVY,
        letterSpacing: '0.015em',
        fontVariantNumeric: 'tabular-nums',
      }}>
        {single ? dateRangeInfo.from : `${dateRangeInfo.from} – ${dateRangeInfo.to}`}
      </Typography>
    </Box>
  );
}

// Single source of truth for "what date range is this page showing" — shared by the
// title chip AND the small applied-range caption next to the Created Date filter
// itself, so the two can never disagree about what's currently applied.
//
// `from`/`to` are full 'dd MMM yyyy' (used by the Created Date caption); `chipFrom`/
// `chipTo` are a shorter 'd MMM' form sized for the page-title chip, which shares its
// cramped TopBar row with the hamburger menu, wallet, and notification icons.
// `dateField` is the row property to read the placed-date from. Defaults to the forward/
// reverse shipment field (awbRegisteredDate); channel orders pass 'orderDate' instead.
export function getOrderDateRangeInfo(filters, rows, { dateField = 'awbRegisteredDate' } = {}) {
  const hasCustomRange = !!(filters?.fromDate && filters?.toDate);

  if (hasCustomRange) {
    const from = formatDate(filters.fromDate, 'dd MMM yyyy');
    const to = formatDate(filters.toDate, 'dd MMM yyyy');
    const chipFrom = formatDate(filters.fromDate, 'd MMM');
    const chipTo = formatDate(filters.toDate, 'd MMM');
    return { hasCustomRange, from, to, chipFrom, chipTo, fromDate: filters.fromDate, toDate: filters.toDate, label: `Showing orders from ${from} to ${to}` };
  }

  if (rows?.length > 0) {
    const times = rows
      .map((r) => new Date(r[dateField]).getTime())
      .filter((t) => !Number.isNaN(t));
    if (times.length > 0) {
      const min = new Date(Math.min(...times));
      const max = new Date(Math.max(...times));
      const from = formatDate(min, 'dd MMM yyyy');
      const to = formatDate(max, 'dd MMM yyyy');
      const chipFrom = formatDate(min, 'd MMM');
      const chipTo = formatDate(max, 'd MMM');
      const label = from === to
        ? `Showing all orders placed on ${to} (your most recent orders)`
        : `Showing your most recent orders, placed between ${from} and ${to}`;
      return { hasCustomRange, from, to, chipFrom, chipTo, fromDate: min, toDate: max, label };
    }
  }

  return { hasCustomRange, from: null, to: null, chipFrom: null, chipTo: null, fromDate: null, toDate: null, label: null };
}
